import { useEffect, useRef } from 'react'
import styles from './TheDifference.module.css'

const contrasts = [
  {
    before: 'Predicts what comes next',
    after: 'Understands what you actually need',
    detail: 'Most AI editors optimize for frequency. Box Editor starts from context: what the codebase is doing, not just what typically follows.',
  },
  {
    before: 'Plausible',
    after: 'Precise',
    detail: "Plausible works until it doesn't, and in careful technical work, it doesn't often enough to matter. Box Editor holds itself to a higher bar.",
  },
  {
    before: 'Suggestion first',
    after: 'Context first',
    detail: 'Other tools put output first and leave you to verify. Box Editor reads the brief before it writes a word, so what you get is a second set of eyes, not a first draft you have to fix.',
  },
]

const ProcessTimeline = () => {
  const sectionRef = useRef<HTMLElement>(null)
  const rowRefs = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    const handleScroll = () => {
      rowRefs.current.forEach((row) => {
        if (!row) return

        const rect = row.getBoundingClientRect()
        const windowHeight = window.innerHeight
        const elementCenter = rect.top + rect.height / 2
        const viewportCenter = windowHeight / 2
        const distanceFromCenter = (elementCenter - viewportCenter) / (windowHeight / 2)

        let progress = Math.max(0, Math.min(1, Math.abs(distanceFromCenter)))
        const deadZone = 0.18
        if (progress < deadZone) {
          progress = 0
        } else {
          progress = (progress - deadZone) / (1 - deadZone)
        }
        const eased = 1 - Math.pow(1 - progress, 3)

        const direction = elementCenter < viewportCenter ? 1 : -1
        row.style.transform = `translateY(${eased * 60 * direction * 0.3}px)`
        row.style.opacity = `${1 - eased * 0.75}`
      })
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <section id="the-difference" ref={sectionRef} className={styles.section}>
      <div className={styles.scanLines} aria-hidden="true" />

      <div className="container">
        <div className={styles.layout}>

          <div className={styles.labelColumn}>
            <div className={styles.sectionTag}>
              <span className={styles.tagNumber}>02</span>
              <span className={styles.tagLine} />
              <span className={styles.tagText}>The Difference</span>
            </div>
          </div>

          <div className={styles.contentColumn}>
            <p className={styles.thesis}>
              What makes Box Editor different?
            </p>

            <div className={styles.contrastStack}>
              {contrasts.map((item, i) => (
                <div
                  key={i}
                  ref={(el) => (rowRefs.current[i] = el)}
                  className={styles.contrastRow}
                  style={{ willChange: 'transform, opacity' }}
                >
                  <div className={styles.beforeSide}>
                    <span className={styles.sideLabel}>them</span>
                    <p className={styles.beforeText}>{item.before}</p>
                  </div>

                  <div className={styles.arrowColumn}>
                    <svg width="32" height="16" viewBox="0 0 32 16" fill="none" className={styles.arrow}>
                      <path
                        d="M0 8 H28 M22 2 L30 8 L22 14"
                        stroke="url(#arrowGrad)"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <defs>
                        <linearGradient id="arrowGrad" x1="0" y1="0" x2="32" y2="0" gradientUnits="userSpaceOnUse">
                          <stop offset="0%" stopColor="#0066FF" stopOpacity="0.4" />
                          <stop offset="100%" stopColor="#00D9FF" />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>

                  <div className={styles.afterSide}>
                    <span className={styles.sideLabel}>Box</span>
                    <p className={styles.afterText}>{item.after}</p>
                    <p className={styles.detail}>{item.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  )
}

export default ProcessTimeline
